/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import React from "react";

type ArrayInputType = {
  value: string[];
  onChange: (prop: string[]) => void;
  placeholder?: string;
  roundedDisabled?: boolean;
  unique?: boolean; // ✅ new prop to prevent duplicates
};

export const ArrayInput = ({
  value,
  onChange,
  placeholder,
  roundedDisabled,
  unique = false,
}: ArrayInputType) => {
  const [text, setText] = React.useState("");

  const addNewItem = () => {
    const trimmed = text.trim();
    if (trimmed === "") return;

    // ✅ handle uniqueness
    if (unique) {
      const exists = value.some(
        (v) => v.toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) {
        setText("");
        return; // skip duplicate
      }
    }

    onChange([...value, trimmed]);
    setText("");
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_item, i) => i !== index));
  };

  const onKeyDownHandle = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addNewItem();
    }
    if (e.key === "Backspace" && text === "") {
      onChange(value.slice(0, -1));
    }
  };

  const displayValue = (val: any) => {
    if (Array.isArray(val)) {
      return val;
    }
    return [];
  };

  return (
    <div
      tabIndex={1}
      className={cn(
        "flex min-h-9 w-full border border-input bg-transparent px-3 py-2.5 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "flex flex-wrap gap-2",
        roundedDisabled ? "rounded-none" : "rounded-md"
      )}
    >
      {displayValue(value)?.map((item, index) => (
        <Badge key={index} variant="outline">
          {item}
          <span
            className="pl-1 cursor-pointer"
            onClick={() => removeItem(index)}
          >
            <X size={10} />
          </span>
        </Badge>
      ))}

      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDownHandle}
        onBlur={addNewItem}
        placeholder={placeholder || "Add new record"}
        className="border-0 bg-transparent outline-none min-h-max flex-1"
      />
    </div>
  );
};
